import Link from 'next/link'
import {
  useRef,
  useMemo,
  useEffect,
  useCallback,
  MouseEventHandler,
} from 'react'
import { Inter } from '@next/font/google'
import { ButtonWithIcon } from '@/components/input'
import { Tooltip } from '@/components/Tooltip'
import { PATHS } from '@/common/const'
import { PageSection } from '@/components/PageSection'
import {
  CheckBadgeIcon,
  CheckCircleIcon,
  CheckIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline'
import { ShieldCheckIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { changeTheme, classNames, setCurrentTheme } from '@/common/utils'
import Image from 'next/image'
import { methods, Step1Method } from './notes/[id]/recipients/new'
import { ChatWidget } from '@/components/ChatWidget'
import { HeadMeta } from '@/components/HeadMeta'
const urbanist = Inter({ subsets: ['latin'] })

const faqs = [
  {
    q: 'What is Backup Diary?',
    a: 'Backup Diary lets you create a private note with passwords, account details, instructions, and wishes that can be delivered to your chosen recipients if you stop checking in.',
  },
  {
    q: 'Who can be a recipient?',
    a: 'Anyone you trust. You can add a spouse, family member, friend, or anyone else using their email address or phone number.',
  },
  {
    q: 'How will they receive my note?',
    a: "Once your note is delivered, we'll convert it into a PDF and send it using the delivery method you chose — email or WhatsApp.",
  },
  {
    q: 'Is it safe to store passwords here?',
    a: 'Yes. Sensitive information is encrypted with AES-256-GCM and stored separately from the rest of your note. It is only decrypted at the moment of delivery. We cannot read it while it is in your account.',
  },
  {
    q: "Can they see my note before it's delivered?",
    a: "No. Your recipients cannot see your note before it's delivered. It stays completely private in your account until you stop checking in and the grace period ends.",
  },
  {
    q: 'What does check-in mean?',
    a: "Checking in simply means signing in to your Backup Diary account so we know you're okay. You can do it from any device, and your account page shows your current check-in status.",
  },
  {
    q: 'What is a check-in period?',
    a: 'Your check-in period is how often you agree to sign in at least once. You can choose a 30, 60, or 90 day check-in period.',
  },
  {
    q: 'Will my note be delivered by accident?',
    a: "We send reminders before your check-in is due, and give you a 7-day grace period after it expires. As long as you sign in before the grace period ends, your note stays private.",
  },
  {
    q: 'Is this a subscription?',
    a: 'No. Backup Diary is a one-time payment that covers up to 20 years of service or until your note is released, whichever comes first.',
  },
  {
    q: 'Is Backup Diary a replacement for a will?',
    a: 'No. A will is still important for legal and estate matters. Backup Diary is for the practical details your family may need quickly, like passwords, account access, bills, instructions, and key contacts.',
  },
  {
    q: 'What if Backup Diary shuts down?',
    a: 'We take the long-term responsibility seriously. If we ever plan to close the service, we will give users enough advance notice to download their note, contact their recipients directly, or make other arrangements. Your note is always accessible from your account dashboard.',
  },
  {
    q: 'I still have more questions.',
    a: "Good, we'd love to answer them. Please reach out to us via chat on the website, or send us an email at help@backupdiary.com",
  },
]

const heroHighlights = [
  'Encrypted — nobody reads it until delivery',
  'One-time payment, no subscription',
  'Delivered by email or WhatsApp',
  'Check in every 30, 60, or 90 days',
]

const practicalDetails = [
  'Passwords and account access',
  'Insurance and banking details',
  'Bills, loans, and subscriptions',
  'Instructions, wishes, and contacts',
]

function QnA({ faq, i }: { faq: { q: string; a: string }; i: number }) {
  const [isOpen, setOpen] = useState(false)

  return (
    <div>
      <dl className="faq mx-auto max-w-2xl">
        <dt className="text-lg">
          <button
            type="button"
            aria-controls={`faq-${i}`}
            onClick={() => setOpen(!isOpen)}
            className="flex w-full items-start justify-between py-6 text-left text-gray-400"
          >
            <span className="font-medium text-gray-900 dark:text-white">
              {faq.q}
            </span>
            <span
              className={`ml-6 flex h-7 items-center ${
                isOpen ? 'rotate-180' : ''
              }`}
            >
              <svg
                className="arrow-down h-6 w-6 rotate-0 transform duration-300"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </span>
          </button>
        </dt>
        <dd
          id={`faq-${i}`}
          className={classNames(
            'faq-answer block overflow-hidden pr-12',
            isOpen ? 'max-h-max' : 'max-h-0',
          )}
        >
          <p className="pb-6 text-base text-gray-600 dark:text-gray-400">
            {faq.a}
          </p>
        </dd>
      </dl>
    </div>
  )
}

function WorksStep({
  i,
  title,
  desc,
  onClick,
  isActive,
}: {
  i: number
  title: string
  isActive: boolean
  onClick: MouseEventHandler<HTMLDivElement>
  desc: string | JSX.Element
}) {
  return (
    <div
      className={classNames(
        'flex items-center gap-6 p-4 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-md cursor-pointer',
        isActive && 'bg-gray-100 dark:bg-gray-800',
      )}
      onClick={onClick}
    >
      <div className="flex h-20 w-20 text-2xl font-bold rounded-xl border border-gray-200 bg-white p-4 dark:text-white dark:border-gray-600/60 dark:bg-gray-900/40">
        {i}.
      </div>
      <div className="w-[calc(100%-7.5rem)]">
        <h4 className="text-lg font-semibold dark:text-white">{title}</h4>
        <div className="mt-1 text-gray-600 dark:text-gray-400">{desc}</div>
      </div>
    </div>
  )
}

function WorksRecipient() {
  return (
    <>
      <p>
        <b>Add another recipient</b>
      </p>
      <p className="mb-4">How should they receive your note?</p>
      <Step1Method value={methods[0]} onChange={() => {}} />
      <div className="flex justify-end">
        <ButtonWithIcon color="blue" disabled>
          Next
        </ButtonWithIcon>
      </div>
    </>
  )
}

function WorksNote() {
  return (
    <div className="space-y-4 text-gray-500 dark:text-gray-400">
      <p>
        Dear Family, I hope this note finds you well. I wanted to take a moment
        to share some important information with you that will help you take
        care of things if I'm not around.
      </p>
      <p>
        I have a life insurance policy with ABC Insurance. The policy number is
        123456789. Please reach out to ABC Insurance at 123-456-7890 if you need
        to file a claim. I have a mortgage with DEF Bank. The mortgage account
        number is 123456789. If you need to make payments or have any questions,
        you can reach out to DEF Bank at 123-456-7890.
      </p>
      <p>
        The passwords of my online accounts are in the section named 'sensitive
        information' below.
      </p>
      <p>
        Finally, I have a safety deposit box at GHI Bank. The key to the box is
        in the top right drawer of my home office desk.
      </p>
    </div>
  )
}

function WorksCheckin() {
  return (
    <>
      <ol className="relative text-gray-500 border-l border-gray-200 dark:border-gray-700 dark:text-gray-400">
        <li className=" mb-16 md:mb-24 ml-8">
          <span className="absolute flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -left-4 ring-4 ring-white dark:ring-gray-900 dark:bg-green-900">
            <CheckIcon className="w-5 h-5 text-green-500 dark:text-green-400" />
          </span>
          <h3 className="font-medium leading-tight h-8 flex flex-col justify-center">
            <span>Last check-in</span>
          </h3>
        </li>
        <li className="mb-8 ml-8">
          <span className="absolute flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-full -left-4 ring-4 ring-white dark:ring-gray-900 dark:bg-yellow-900"></span>
          <h3 className="font-medium leading-tight">
            <span>60 days</span>
          </h3>
          <p className="text-xs">Your check-in resets this period</p>
        </li>
        <li className="mb-8 ml-8">
          <span className="absolute flex items-center justify-center w-8 h-8 bg-yellow-200 rounded-full -left-4 ring-4 ring-white dark:ring-gray-900 dark:bg-yellow-700"></span>
          <h3 className="font-medium leading-tight">Grace period</h3>
          <p className="text-xs">We'll give you 7 more days to check-in</p>
        </li>
        <li className="ml-8">
          <span className="absolute flex items-center justify-center w-8 h-8 bg-red-300 rounded-full -left-4 ring-4 ring-white dark:ring-gray-900 dark:bg-red-700"></span>
          <h3 className="font-medium leading-tight h-8 flex flex-col justify-center">
            Note sent to recipients
          </h3>
        </li>
      </ol>
    </>
  )
}

const stepWorks = [WorksNote, WorksRecipient, WorksCheckin]

function HowItWorksSteps() {
  const [current, setCurrent] = useState(0)
  const WorksComponent = useMemo(() => stepWorks[current], [current])

  return (
    <div className="gap-6 space-y-12 md:flex md:space-y-0">
      <div className="relative md:w-1/2">
        <div
          className="panel visible inset-0 flex scale-100 flex-col justify-center opacity-100 transition duration-500 md:absolute"
          id="panel-0"
        >
          <div>
            <div className="mt-12 space-y-10">
              {[
                {
                  title: 'Write your note',
                  desc: "Passwords, accounts, insurance details, instructions, and personal wishes. Everything stays encrypted and private until it's time.",
                },
                {
                  title: 'Add recipients',
                  desc: 'Choose the people you trust and add their email address or phone number.',
                },
                {
                  title: 'Check-in',
                  desc: (
                    <>
                      Sign in just once every{' '}
                      <Tooltip
                        label="You can select between the 30, 60, and 90 days check-in period."
                        obvious
                        placement="top"
                      >
                        60 days
                      </Tooltip>
                      . If you ever stop checking in, we deliver it
                      automatically.
                    </>
                  ),
                },
              ].map((step, i) => (
                <WorksStep
                  key={i}
                  i={i + 1}
                  title={step.title}
                  desc={step.desc}
                  isActive={current == i}
                  onClick={() => {
                    setCurrent(i)
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="-m-4 overflow-hidden p-4 sm:-mx-12 sm:px-12 md:mx-0 md:w-1/2 md:overflow-visible md:px-0">
        <div className="relative bg-gray-100 before:absolute before:inset-0 before:scale-x-110 before:border-y before:border-gray-200 after:absolute after:inset-0 after:scale-y-110 after:border-x after:border-gray-200 dark:bg-gray-800 dark:before:border-gray-700 dark:after:border-gray-700">
          <div className="relative h-96 overflow-clip py-10 sm:h-[32rem] lg:p-20">
            <div className="panel-preview absolute inset-0 z-10 flex translate-y-0 scale-100 items-end overflow-hidden px-6 opacity-100 transition duration-500 sm:px-10">
              <div className="mx-auto h-80 w-96 p-8 space-y-4 rounded-t-3xl border select-none bg-white dark:border-gray-700/60 dark:bg-gray-900 dark:text-gray-300 object-cover object-top shadow-2xl sm:h-[28rem]">
                <WorksComponent />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function NavBarHome() {
  const [active, setActive] = useState(false)

  return (
    <nav
      id="navbar"
      className={classNames(
        'fixed inset-x-0 z-20 w-full border-b border-gray-100 bg-white/80 backdrop-blur dark:border-gray-700/30 dark:bg-gray-900/80',
        active && 'navbar-active',
      )}
    >
      <div className="mx-auto px-4 sm:px-12 xl:max-w-6xl xl:px-0">
        <div className="relative flex flex-wrap items-center justify-between gap-6 lg:gap-0 lg:py-4">
          <div className="relative z-20 flex w-full justify-between md:px-0 lg:w-max">
            <a
              href="./"
              aria-label="backupdiary logo"
              className="nav-link flex items-center space-x-2"
            >
              <Image
                width={20}
                height={20}
                src="/icon.svg"
                alt="BackupDiary logo"
                className="h-8 w-8"
              />
              <b className="text-lg text-gray-600 dark:text-gray-300">
                Backup Diary
              </b>
            </a>

            <button
              id="hamburger"
              aria-label="humburger"
              onClick={() => setActive(!active)}
              className="relative -mr-6 p-6 lg:hidden"
            >
              <div
                id="line"
                aria-hidden="true"
                className="m-auto h-0.5 w-5 rounded bg-blue-900 transition duration-300 dark:bg-gray-300"
              ></div>
              <div
                id="line2"
                aria-hidden="true"
                className="m-auto mt-2 h-0.5 w-5 rounded bg-blue-900 transition duration-300 dark:bg-gray-300"
              ></div>
            </button>
          </div>
          <div
            id="layer"
            aria-hidden="true"
            className="fixed inset-0 z-10 h-screen w-screen origin-bottom scale-y-0 bg-white/70 backdrop-blur-2xl transition duration-500 dark:bg-gray-900/70 lg:hidden"
          ></div>
          <div
            id="navlinks"
            className="invisible absolute top-full left-0 z-20 w-full origin-top-right translate-y-1 scale-90 flex-col flex-wrap justify-end gap-6 rounded-3xl border border-gray-100 bg-white p-8 opacity-0 shadow-2xl shadow-gray-600/10 transition-all duration-300 dark:border-gray-700 dark:bg-gray-800 dark:shadow-none lg:visible lg:relative lg:flex lg:w-auto lg:translate-y-0 lg:scale-100 lg:flex-row lg:items-center lg:gap-0 lg:border-none lg:bg-transparent lg:p-0 lg:opacity-100 lg:shadow-none dark:lg:bg-transparent"
          >
            <div className="text-gray-600 dark:text-gray-300 lg:pr-4">
              <ul className="space-y-6 text-base font-medium tracking-wide lg:flex lg:space-y-0 lg:text-sm">
                <li>
                  <a
                    href="#works"
                    onClick={() => setActive(false)}
                    className="nav-link block transition hover:text-blue-600 dark:hover:text-blue-500 md:px-4"
                  >
                    <span>How it works</span>
                  </a>
                </li>
                <li>
                  <a
                    href="#pricing"
                    onClick={() => setActive(false)}
                    className="nav-link block transition hover:text-blue-600 dark:hover:text-blue-500 md:px-4"
                  >
                    <span>Pricing</span>
                  </a>
                </li>
                <li>
                  <a
                    href="#faqs"
                    onClick={() => setActive(false)}
                    className="nav-link block transition hover:text-blue-600 dark:hover:text-blue-500 md:px-4"
                  >
                    <span>FAQs</span>
                  </a>
                </li>
              </ul>
            </div>

            <div className="mt-12 -ml-1 flex w-full flex-col space-y-2 border-blue-600/10 dark:border-gray-700 sm:flex-row md:w-max lg:mt-0 lg:mr-6 lg:space-y-0 lg:border-l lg:pl-6">
              <Link href={PATHS.HOME} className="btn">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}

function AudioExplainer() {
  const [playing, setPlaying] = useState(false)
  const audio = useRef<HTMLAudioElement | null>(null)
  useEffect(() => {
    audio.current = new window.Audio('/works.mp3')
    audio.current?.addEventListener('ended', () => {
      setPlaying(false)
    })
  }, [])

  const toggle = useCallback(() => {
    if (playing) {
      audio?.current?.pause()
    } else {
      audio?.current?.play()
    }

    setPlaying(!playing)
  }, [playing])

  return (
    <ButtonWithIcon onClick={toggle} classes="font-medium flex justify-start">
      <span>Hear how it works</span>
      <SpeakerWaveIcon
        className={`w-4 max-h-5 ml-2 ${
          playing && 'animate-pulse text-blue-500'
        }`}
      />
    </ButtonWithIcon>
  )
}

export default function LandingHome() {
  useEffect(setCurrentTheme, [])

  return (
    <>
      <HeadMeta
        title="One private note for the people who matter most"
        description="Write your passwords, accounts, insurance details, and wishes in a private encrypted note. Choose who receives it. If you stop checking in, Backup Diary delivers it to them automatically."
      />
      <div
        className={`min-h-screen scroll-smooth bg-white selection:bg-blue-600/10 selection:text-blue-600 dark:bg-gray-900 ${urbanist.className}`}
      >
        <header>
          <NavBarHome />
        </header>

        <section className="pt-48 pb-24">
          <div className="mx-auto px-4 sm:px-12 xl:max-w-6xl xl:px-0">
            <div className="relative grid grid-cols-1 md:grid-cols-2">
              <div>
                <h1 className="mt-2 text-center text-5xl font-bold text-blue-900 dark:text-white sm:text-6xl lg:text-left lg:text-7xl max-w-xl">
                  Your family shouldn&apos;t have to{' '}
                  <span className="relative">
                    <svg
                      className="absolute inset-x-0 -bottom-1 w-full opacity-50"
                      xmlns="http://www.w3.org/2000/svg"
                      id="Layer_1"
                      viewBox="0 0 260 15.6"
                    >
                      <path
                        className="fill-blue-500"
                        d="M206.8 7.3l-.1.3c.1-.2.2-.3.1-.3zM234.7 10h-.1c-.2.4-.1.3.1 0zM54.8 4.2l-.6-.4c.2.4.4.5.6.4zM17.1 5.1zM34.5 9.6l.1.3c0-.2 0-.3-.1-.3zM22.4 10.8c-.3-.1-.7-.1-1-.1.2.1.7.1 1 .1zM17.5 5c-.1.1-.2.1-.4.2.2-.1.3-.2.4-.2zM52.7 9.8l.5.9c-.1-.3-.3-.6-.5-.9zM19.5 11.6c-.2-.2-.4-.2-.6-.3 0 .2.3.3.6.3zM120.9 11.4c-.1.1-.2.2-.2.3.3-.1.3-.2.2-.3zM80.9 10.4h-.1s.1.1.2.1l-.1-.1zM92.6 10.4l-.2.2c.2-.1.2-.1.2-.2zM72.1 11.3c-.1.1-.3.2-.4.3l.4-.3z"
                      />
                      <path
                        className="fill-blue-500"
                        d="M260 6c-1-.6-4.7-1.2-5.8.3-.2-.1.1-.3.2-.4-.9.2-2.2.1-3.6 0s-2.9-.2-4.2 0c-1 1.5-3.9-.6-4.8 1.4l.5-.4c.9.5-1.2 1.4-1.5 1.9-.8-1.2-.1-1-1-2l1.1.4-.3-1c-3.1 2.8-6.2-.9-8.2 1.1.1-.1.1-.3.2-.4-1.4-.5-2.3.8-3.3 1.2-.1-.5.6-.9 1.1-1.3-2.4-.3-6.4 1.2-9 .4-.9.7.4.9-.6 1.5-.8-.2-1.4-.7-.4-1.1-2.3-1.2-7.6 1-11.1-.2-1.8.8-.7 1.1-3.5 1.6.7-.5-.7-1.7 1-1.7l.2-.5c-2.8-.1-6.6-.3-8.1 1.2-.1-1.1-.5-.2-1.6-.8-.4.1 0 .2.2.2-1 .9-1.6-.1-2.3.1l.3-.2-2 .7c-.3-.2-.8-.4-.9-.7v.8c-1.1 0-.5-1-1.9-.8l.3.6c-.9-.4-2.2.4-2.4-.5 0-.2.1-.1.4-.1-1.3-1.2-3.5.3-5.1-.3l.4 1.3c-1.6.4-1-.3-.9-.7-1.1 0-1.3-.4-2.7-.6-.7.3-.4.5-.6.8l-1.5-.4 1-.7c-2.3 1.8-5.6-.4-7.2 1.2-.8-.4.8-.7.3-1-2.6-.9-6 1-8.2 0-3.6-1-7.8-.4-11.8-1.1l.1.3-2.9-.4c-.8.7-2.7.3-4 1.1.1-.3-.1-.7.2-.9-1.2.1-2.6.4-3.3-.1l.4-.3c-2.7-.3-6.4-.5-7.9.1-.9 0-.9-.6-1.1-1-1.6-.1-2.6.2-3.9.7-.3-.2-.7-.3-1-.6l-.6.8c-.6-.1-.1-.7-.6-.9-2.5.9-5.3-.1-7-.1l.2.4c-.7.3-2.1-.3-1.2-.7-3.4-.6-5.1 1.2-9.6.8-.6-1.5-4.1.3-4.8-1.4-1.9.4-3.2-.3-4.5.6 0-.2-.2-.2.1-.3-.8-.6-3.3-.2-5.3.2l-.1-.5c-.9 1.2-4.2.9-4.9 2-.2-.2.4-.5.7-.7-1-1.1-1.8.5-3.1.2.1-.3-.3-.6 0-.8-4.4-1.2-10.6.7-16.3-.1-1.6 0 .6 1.2-1.5 1.1-.6-.6 1-1.1-.3-1.4-.9.7-1.3.5-2.6.5.2-.4 0-.6.9-.9-.7-.5-3.1.9-4.5 0 .1.3-.2.5-.5.7-2.1 1-4.9-.9-5.1-.4 0 0-.7.2-.1.3-.8 0-1.9-.2-1.7-.7-.4.3-.8.8-1.4.8l.3-.6c-.4.1-.8.5-1.1.6l.6.4c-.9-.5-2.6.8-2.6-.4h.3l-1.7-.5c-.7.5-1.3 1-2.5.9-.5-1.3-2.9-.2-4.3-.3l.1-.4c-1.1.6-4 .4-3.5.6-1.1 0-2.6-.2-2-.6-.8.1-2.7.1-3.2.9l-1.8-1c-1 1.6-3.6-.5-3.6 1.2-1-.2-.8-.6-1.5-.9-1.4.9-2.8.8-4.2.7v-.2c-1.4-.1-3.1.8-5.1 1l-.5-1.2c-1 .2-1.3 1.2-2.3 1-.2-.2 0-.3.2-.3-1 .3-2.3.1-3.1-.2-1.5 1-2.7.7-3.9 1.8-1.3-1 1.7-.6.6-1.6-2.2-.4-4.4.4-6.7 1.1-.2-.2 0-.4.1-.7 0 0-1.2.9-2.2 1.8C.9 8.3 0 9.4.5 10c-.5.9-1.2 1.4.9 2 .6-.5 2.5-1.3 2.9-.4l.1-.9c2.6-.6.4 1.8 3.6 1.6l-.7-.3c.6-.1 1.1-.7 1.8-.5.2.2-.2.4-.5.6.9-.5 1.7-.9 2.6-1.4.1.5.1.8-.4 1.2 2.5-.2-.6-1.6 2.4-1.4.6.4-.2.6-.5.9 1.4.7 2.3-.1 3.8-.6.1.8-.9.7.3 1.2-.3-.4-.5-1.1.5-1.2-.4.8.7.4 1.6.5-.2-.3-.1-.6.2-.8.4-.1.8.1 1.4.1l-1.1-.7c1.5-.8 2.4.3 3.6.6-.1.1-.3.3-.5.3 1.2.3 2.5.9 4.1.1l-.3.1c2.9-.9-1-1.3 2.4-2.2 1.1.1-.4 2.6 2.1 1.6-1.3-.6 1.6-1.7 3.1-2L32.4 10c.6 0 1.6-.5 2-.3l-.1-.3c-.2-1.3 1.9.1 3-.7-1.3 1.8-1.4 1.5-1.6 3.2 1-1 2.2-1.9 4.1-1.8l-1.5 1.4c2.5.2 5.5-1.9 7.6-3-.5 1 .3 1.4-.6 2.2l2.4-.3-.7 1.1c1-1.2 2.1-.4 1.9-1.9-.3.2-.2.4-.7.3.1-.4.5-1.4 1.7-1.3.9.3-.5.6-.2 1 .8-.6.9.3 1.7-.1l-.8-.6c.6-.9 1.4-.1 2.2-.5-1 .4-.7.9-.3 1.4l-.1-.1c.8-.1 1.6-.7 2-.2l-.5 1.2.9-.9c.3.1.6.6 0 .8 2.8.7-.1-2.5 3.6-1.5 0 .5-.4.8-1.4.5-.2.7.1 1.1 1.1 1.4v.1c1.9 0 4.4 0 5.6-.8.4.3 0 .6-.4.9 2.1.4 2.8-.7 5 .1l-1-.4c1.4-.6 4-.8 5.3.1l-.4.3c1.3-.7 3.5.6 4 0-.6-.4 0-.6-.8-1l3.4-.7.2 1.2 1.8-.4c-.4-.5 2.4.4 2.5-.7 1 .4-.4.9-.8 1.4 1-.3 1.1.2 2.1-.5l1 1.1 2.6-.7c-.1.1 0 .2-.1.3 1.2-.9 3.1.6 4.6-.9-.1.1-.1.1-.1.2.9-.8 2.9-.2 3.7 0 1.4-.2.6-1 .6-1.4 3.9.4 2.7.3 6-.9 2 1.4-2.4 2.1.1 3 .4-.6 2.1-1.1 4.1-1.3 1.8.5 4.8.9 6.5 1.9l-.2-.9 2.6-.4-1.5 1.2c.4-.3 1.7-.8 2.6-1.2 2.7-.7 1.4 1.9 3.5.7.1.1.1.2.2.3.7-.6 2.4-.3 4.4-.5l-.7 1.1-1.3-.3c.7 1.1 2.1-.1 3.4 0 1.3-.3.7-1.3 1.4-1.6.5.1 1.2-.2 1.6.1 1.1.4.1 1.3-.3 1.8 1-1.1 1.4-.9 3.6-1.3.1.5-.1.8-.4.9.5-.1.9-.3 1.2-.8l.7.7c2.5 1 2.6-2 5.6-1.5-.8.6 2.6 0 3.5.7-1.1.1.4 1.6-.2 2.3 2.4.5 1-1.3 3-1.4l-.9 1.3c1.9-.5.5-.7 2.4-1.1-.5.4.8.4-.3.8 2.5.2 1.9.1 4.1.3l.2-1.3c.7-.1 1 .5 1.2.7-.3 0-.8-.1-.7.1.8 1.2 1.4-.6 2.4.5-.2-.4-.5-1 .4-1.1-.3.8 1.4.8 1.4 1.2-.6-.6 2-.2 2.1-1 1 .7-.4.6-.4 1.1.9-1 3.7 0 4.6-.6 0 .1.1.1.1.2 1.2-.6 3-.7 5.3-1.5l-.8.7c2.2.4 1.4-1.5 3.3-1.5-.4 1.1 3.1 0 2.2 1.2 1.1-.6 2.3-.8 3.1-1.7 1 .6-2.1 1.4-.6 1.8l1.6-.5.3.6c.1-.4 1.5-.4 1.4-.8.2.7.9 1.2.8 1.8 1-.2 2.4.5 3.3-.1l.1.3c1-1.3 3.1-.2 3.6-1.5l.6.7c1.5-.1 1.3-1.5 2-1.8.6 0 1.4-.2 2 0-2 .8 1 1.1 1.4 1.6.8 0 3.1 0 3.7-.7-1 .7-.4 1.2-2.1 1.3.9 1.3 2.6-.2 4.5-.1v.6c2.7-.4 2.8-1.5 4-2.5.3.8.1 1-.7 1.7 1.8.5 4.7-.1 6.7 0 .6.5.2.9-.5 1.1 2.1-.6 4.7.1 6-1.2-.5.5.9.3 1.6.6 0-.3.1-.6.2-.6 1.3-.6 4.1-1.1 5.6-.7l-.5.4c1.7.1 3-.5 4.3-.9 1.3-.4 2.6-.8 4.5-.4.7.3-.7 1.1.7 1 .7-.5.4-1.5 2.2-1.3l-.1.9 1.2-.9c-.7-.7-2.6-.4-1.3-1.2 1.6.8 1.3-.9 3.3 0-.4.1-1 .8-1.3 1.2 2 .4 3.4.1 4.8-.1 1.4-.3 2.8-.6 4.9-.2 2-.8 4.6-1.2 5.9-1.9 0 .9 0 1.7-.8 2.4 1.8 0 2.4-2.1 3.7-.9.7-1.3 4.7-1.2 5-3l2-.8z"
                      />
                      <path
                        className="fill-blue-500"
                        d="M58.1 11.1c-1 0-1.9 0-2.3.2.2.2 2.3.6 2.3-.2zM208.2 13.3c-.1 0-.3.1-.4.1.1 0 .3 0 .4-.1zM216.3 12.9c-.1-.1-.2-.2-.4-.3 0 .3.1.5.4.3zM132.6 11.5zM178.5 13.7c.7-.4 1-.7 1-1-.4.1-.7.3-1 1zM163 12.6c-.1.1-.2.1-.3.2.3-.1.3-.2.3-.2zM130.2 12c.7-.4 1.6-.3 2.4-.5-.7.2-1.9-.3-2.4.5zM226.1 11.4l-.7.6.8-.4zM218.6 12c-.3-.1-1.7.3-1.3.6.4-.3.9-.5 1.3-.6zM189.6 11.4l-.3.6.7-.5z"
                      />
                    </svg>
                    <span className="relative bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-blue-600">
                      figure it out alone
                    </span>
                  </span>
                </h1>

                <div className="relative md:mt-16 py-8">
                  <div className="text-center md:text-left">
                    <p className="mt-6 md:mt-0 max-w-lg text-lg text-gray-600 dark:text-gray-200">
                      Write down your passwords, accounts, insurance details,
                      and personal wishes. Pick who receives it. If you stop
                      checking in, we deliver it to them automatically.
                    </p>
                    <div className="mt-8 md:mt-6">
                      <div className="relative flex flex-wrap items-center justify-center gap-4 md:justify-start">
                        <Link href={PATHS.HOME} className="btn green">
                          Write your note
                        </Link>
                        <AudioExplainer />
                      </div>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-500 dark:text-gray-400 justify-center md:justify-start">
                      {heroHighlights.map((item) => (
                        <span key={item} className="flex items-center gap-1.5">
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative flex justify-center items-center pt-8 md:pt-0">
                <div className="absolute inset-x-0 top-10 mx-auto h-72 w-72 rounded-full bg-blue-200/60 blur-3xl dark:bg-blue-700/20" />
                <Image
                  priority
                  className="relative w-4/5 max-w-lg"
                  width={'100'}
                  height={'100'}
                  src="/landing/hero.svg"
                  alt="an illustration showing a couple of sticky notes"
                />
              </div>
            </div>
          </div>
        </section>

        <section
          id="works"
          className="relative pt-12 border-t border-gray-100 dark:border-gray-800"
        >
          <div className="mx-auto px-4 sm:px-12 xl:max-w-6xl xl:px-0">
            <div className="text-center">
              <h2 className="text-3xl font-bold dark:text-white md:text-4xl xl:text-5xl">
                Three steps. Then{' '}
                <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                  it takes care of itself
                </span>
              </h2>
            </div>
            <div className="mt-16">
              <HowItWorksSteps />
            </div>
          </div>
        </section>

        <section className="mt-16 md:mt-32 relative before:absolute before:inset-0 before:h-px before:w-96 before:bg-gradient-to-r before:from-yellow-300 before:via-pink-400 before:to-transparent after:absolute after:inset-0 after:ml-auto after:mt-auto after:h-px after:w-96 after:bg-gradient-to-l after:from-green-300 after:via-pink-400 after:to-transparent">
          <div className="border-y border-gray-200 dark:border-gray-700 dark:bg-gray-900">
            <div className="mx-auto px-4 md:max-w-full my-8 md:px-12 lg:max-w-6xl xl:px-0">
              <h2 className="text-2xl font-bold text-gray-700 dark:text-white lg:text-3xl mb-1">
                Everything they&apos;ll need.{' '}
                <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                  All in one place
                </span>
              </h2>
              <p className="mb-8 text-lg text-gray-600 dark:text-gray-300">
                The details that are easy to forget and hard to find in a
                crisis.
              </p>
              <div className="flex flex-row flex-wrap gap-4 justify-center items-center md:justify-start my-8">
                <span className="px-4 py-2 bg-blue-100 text-gray-800 text-sm font-medium rounded-lg">
                  Passwords
                </span>
                <span className="px-4 py-2 bg-blue-100 text-gray-800 text-sm font-medium rounded-lg">
                  Insurance information
                </span>
                <span className="px-4 py-2 bg-blue-100 text-gray-800 text-sm font-medium rounded-lg">
                  Secret keys
                </span>
                <span className="px-4 py-2 bg-blue-100 text-gray-800 text-sm font-medium rounded-md">
                  Property information
                </span>
                <span className="px-4 py-2 bg-green-100 text-gray-800 text-sm font-medium rounded-lg">
                  Things owed or lent
                </span>
                <span className="px-4 py-2 bg-green-100 text-gray-800 text-sm font-medium rounded-lg">
                  Subscription to cancel
                </span>
                <span className="px-4 py-2 bg-green-100 text-gray-800 text-sm font-medium rounded-lg">
                  Wishes
                </span>
                <span className="px-4 py-2 bg-yellow-100 text-gray-800 text-sm font-medium rounded-lg">
                  Instructions
                </span>
                <span className="px-4 py-2 bg-yellow-100 text-gray-800 text-sm font-medium rounded-lg">
                  Loans to pay off
                </span>
                <span className="px-4 py-2 bg-yellow-100 text-gray-800 text-sm font-medium rounded-md">
                  Banking details
                </span>
                <span className="px-4 py-2 bg-yellow-100 text-gray-800 text-sm font-medium rounded-md">
                  Business information
                </span>
                <span className="px-4 py-2 bg-red-100 text-gray-800 text-sm font-medium rounded-md">
                  Online accounts
                </span>
                <span className="px-4 py-2 bg-red-100 text-gray-800 text-sm font-medium rounded-md">
                  Mortgage
                </span>
                <span className="px-4 py-2 bg-red-100 text-gray-800 text-sm font-medium rounded-md">
                  Vehicle information
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="pt-16 md:pt-32 space-y-24">
          <div className="mx-auto px-4 sm:px-12 xl:max-w-6xl xl:px-0">
            <div className="items-center justify-center space-y-6 md:flex md:gap-6 md:space-y-0">
              <div className="md:w-1/2 lg:w-3/5 flex justify-center">
                <Image
                  className=" w-4/5 shadow-sm rounded-md object-cover md:-ml-8"
                  src="/landing/release.jpg"
                  alt="release settings"
                  loading="lazy"
                  width={'1000'}
                  height={'500'}
                />
              </div>
              <div className="md:w-1/2">
                <h2 className="mb-8 text-4xl font-bold text-gray-700 dark:text-white lg:text-5xl">
                  <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                    You are in control
                  </span>
                  <span className=""> until you aren't</span>
                </h2>
                <p className="text-gray-600 dark:text-gray-300 text-lg">
                  Your note stays locked until you stop checking in. You decide
                  what it says, who receives it, and how often you sign in.
                  Update it anytime life changes.
                </p>
              </div>
            </div>
          </div>
          <div className="mx-auto px-4 sm:px-12 xl:max-w-6xl xl:px-0">
            <div className="items-center justify-center space-y-6 md:flex md:gap-6 md:space-y-0">
              <div className="md:w-1/2">
                <h2 className="mb-8 text-3xl font-bold text-gray-700 dark:text-white lg:text-4xl">
                  <span className="">Regular </span>
                  <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                    Reminders
                  </span>
                </h2>
                <p className="text-gray-600 dark:text-gray-300 text-lg">
                  We remind you before your check-in is due. And if you miss the
                  deadline, you get 7 more days before anything is sent. No
                  surprises, no accidents.
                </p>
              </div>
              <div className="md:w-1/2">
                <div className="p-8">
                  <div className="shadow-sm rounded-md border p-10 bg-gray-100">
                    <div className="shadow-md">
                      <PageSection heading="Check-in to Backup Diary">
                        <p className="text-center italic p-4">
                          You&apos;re due for a quick check-in. Sign in now to
                          keep your note private and make any updates you want.
                        </p>
                      </PageSection>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-auto px-4 sm:px-12 xl:max-w-6xl xl:px-0">
            <div className="items-center justify-center space-y-6 md:flex md:gap-6 md:space-y-0">
              <div className="md:w-1/2">
                <div className="p-8">
                  <div className="shadow-sm rounded-md border p-6 border-gray-100 bg-white dark:border-gray-700/60 dark:bg-gray-800 dark:text-gray-100">
                    <div className="flex justify-center mb-4">
                      <CheckBadgeIcon className="w-24 h-24 text-blue-50 bg-gradient-to-b from-green-500 to-green-600 rounded-md p-2" />
                    </div>
                    <p className="text-center mt-4">
                      We convert your note into a PDF document and send it to
                      all recipients.
                    </p>
                  </div>
                </div>
              </div>
              <div className="md:w-1/2">
                <h2 className="mb-8 text-3xl font-bold text-gray-700 dark:text-white lg:text-4xl">
                  <span className="bg-gradient-to-r from-yellow-400 to-red-600 bg-clip-text text-transparent">
                    Automatic
                  </span>
                  <span className=""> Delivery</span>
                </h2>
                <div className="text-gray-600 dark:text-gray-300 text-lg">
                  When your check-in period ends and the{' '}
                  <Tooltip
                    label="We wait seven more days after your main check-in period expires. You can still check in during the grace period."
                    obvious
                  >
                    7-day grace period
                  </Tooltip>{' '}
                  passes, we convert your note into a PDF and deliver it to your
                  recipients — by email or WhatsApp.
                </div>
              </div>
            </div>
          </div>

          <div className="mx-auto px-4 sm:px-12 xl:max-w-6xl xl:px-0">
            <div className="items-center justify-center space-y-6 md:flex md:gap-6 md:space-y-0">
              <div className="md:w-1/2">
                <h2 className="mb-8 text-3xl font-bold text-gray-700 dark:text-white lg:text-4xl">
                  <span className="bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent">
                    Secure
                  </span>
                  <span className=""> by-default</span>
                </h2>
                <p className="text-gray-600 dark:text-gray-300 text-lg">
                  Your note is encrypted with AES-256 — the same standard used
                  by banks. We cannot read it, and neither can anyone else.
                  It&apos;s only decrypted at the moment of delivery.
                </p>
              </div>
              <div className="md:w-1/2">
                <div className="p-8">
                  <div className="shadow-sm rounded-md border p-6 border-gray-100 bg-white dark:border-gray-700/60 dark:bg-gray-800 dark:text-gray-100">
                    <div className="flex justify-center">
                      <ShieldCheckIcon className="w-28 h-28 text-green-600" />
                    </div>
                    <p className="text-center mt-4">
                      We use the Advanced Encryption Standard 256-bit key with
                      authentication data for at-rest encryption of your
                      sensitive information.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pt-20 relative">
          <div className="mx-auto px-4 sm:px-12 xl:max-w-6xl xl:px-0">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-800 dark:text-white md:text-4xl xl:text-5xl">
                Not a will.{' '}
                <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                  A practical companion to one.
                </span>
              </h2>
            </div>
            <div className="mt-12 grid gap-8 md:grid-cols-2">
              <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-2xl shadow-gray-600/10 dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  What a will usually handles
                </h3>
                <ul className="mt-6 space-y-4 text-gray-600 dark:text-gray-300">
                  <li className="flex items-start gap-3">
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                    Legal distribution of assets
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                    Guardianship and formal estate wishes
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                    Official instructions for courts and executors
                  </li>
                </ul>
              </div>

              <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-2xl shadow-gray-600/10 dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  What Backup Diary helps with
                </h3>
                <ul className="mt-6 space-y-4 text-gray-600 dark:text-gray-300">
                  {practicalDetails.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="pricing" className="overflow-x-clip pt-32">
          <div className="mx-auto px-4 sm:px-12 xl:max-w-5xl xl:px-0">
            <div className="relative z-10 text-center md:mx-auto md:w-5/6 lg:w-4/6">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white md:text-4xl xl:text-5xl">
                One payment. Yours until it&apos;s needed.
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-gray-600 dark:text-gray-300">
                No subscription. No renewals. Pay once, never again.
              </p>
            </div>
            <div className="relative mt-20">
              <div
                aria-hidden="true"
                className="absolute inset-0 top-15 mx-auto mt-auto h-[20rem] w-[25rem] rounded-full bg-blue-400/50 blur-3xl dark:bg-blue-700/40 md:m-auto"
              ></div>
              <div className="m-auto items-center justify-center -space-y-4 md:flex md:space-y-0 md:-space-x-1">
                <div className="relative z-10 -mx-2 rounded-3xl border border-gray-100 bg-white shadow-2xl shadow-gray-600/10 dark:border-gray-700/60 dark:bg-gray-800 dark:shadow-none md:mx-0 md:w-6/12 lg:w-5/12">
                  <div className="space-y-6 p-4 sm:p-8">
                    <div className="flex justify-center flex-col items-center">
                      <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        One-time payment
                      </span>
                      <div className="flex items-end space-x-0.5">
                        <span className="pb-1 text-xl text-gray-700 dark:text-gray-300">
                          $
                        </span>
                        <span className="leading-0 text-5xl font-bold text-gray-800 dark:text-white">
                          89
                        </span>
                        <span className="pb-1 text-gray-700 dark:text-gray-300">
                          USD
                        </span>
                      </div>
                      <div className="text-lg">
                        <span className="font-bold text-green-500 dark:text-green-500">
                          <sup className="text-gray-600">*</sup>
                          up to 20 years{' '}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300">
                          of coverage
                        </span>
                      </div>
                      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                        Less than $4.50 per year across the full term.
                      </p>
                    </div>
                    <ul
                      role="list"
                      className="m-auto w-max space-y-4 py-4 text-gray-600 dark:text-gray-300"
                    >
                      <li className="flex justify-start items-center space-x-2">
                        <CheckCircleIcon className="w-4 h-4 inline-block" />
                        <span>
                          Private, encrypted note — nobody reads it until
                          delivery
                        </span>
                      </li>
                      <li className="flex justify-start items-center space-x-2">
                        <CheckCircleIcon className="w-4 h-4 inline-block" />
                        <span>One-time payment, not a subscription</span>
                      </li>
                      <li className="flex justify-start items-center space-x-2">
                        <CheckCircleIcon className="w-4 h-4 inline-block" />
                        <span>
                          Check-in reminders with a 7-day grace period
                        </span>
                      </li>
                      <li className="flex justify-start items-center space-x-2">
                        <CheckCircleIcon className="w-4 h-4 inline-block" />
                        <span>Update your note and recipients anytime</span>
                      </li>
                      <li className="flex justify-start items-center space-x-2">
                        <CheckCircleIcon className="w-4 h-4 inline-block" />
                        <span>Delivered by email or WhatsApp as a PDF</span>
                      </li>
                    </ul>
                    <div className="flex flex-col justify-center items-center space-y-3">
                      <Link href={PATHS.HOME} className="btn green">
                        Write your note
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="faqs" className="my-32">
          <div className="mx-auto px-4 sm:px-12 xl:max-w-5xl xl:px-0">
            <h2 className="mb-12 text-center text-3xl font-bold text-gray-800 dark:text-white md:text-4xl xl:text-5xl">
              Frequently Asked Questions
            </h2>
            <div className="divide-y divide-gray-200 border-y border-gray-200 dark:divide-gray-800 dark:border-gray-800">
              {faqs.map((faq, i) => (
                <QnA faq={faq} key={i} i={i} />
              ))}
            </div>

            <p className="text-gray-500 dark:text-gray-400 mt-8">
              <sup>*</sup> lifetime refers to 20 years or until the note is
              released, whichever comes first.
            </p>
          </div>
        </section>

        <section className="mt-16 md:mt-32 relative before:absolute before:inset-0 before:h-px before:w-96 before:bg-gradient-to-r before:from-yellow-300 before:via-pink-400 before:to-transparent after:absolute after:inset-0 after:ml-auto after:mt-auto after:h-px after:w-96 after:bg-gradient-to-l after:from-green-300 after:via-pink-400 after:to-transparent">
          <div className="border-y border-gray-200 dark:border-gray-700 dark:bg-gray-900">
            <div className="mx-auto md:max-w-full my-8 p-6 md:px-12 lg:max-w-6xl xl:px-0">
              <div className="grid md:grid-cols-2 grid-cols-1">
                <div>
                  <h2 className="text-2xl font-bold text-gray-700 dark:text-white lg:text-3xl mb-2">
                    Don't leave it to{' '}
                    <span className="bg-gradient-to-r from-gray-500 to-gray-100 dark:from-gray-50 dark:to-gray-800 bg-clip-text text-transparent">
                      tomorrow
                    </span>
                  </h2>
                  <p className="text-lg text-gray-700 dark:text-white">
                    There may not even be one.
                  </p>
                </div>
                <div className="space-y-4 md:mt-0 mt-8">
                  <Link href={PATHS.HOME} className="btn green">
                    Write your note now
                  </Link>
                  <p className="text-lg text-gray-700 dark:text-white">
                    Your family will know exactly where to start.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="border-t border-gray-100 pt-16 pb-8 dark:border-gray-800">
          <div>
            <div className="m-auto space-y-8 px-4 text-gray-600 dark:text-gray-400 sm:px-12 xl:max-w-6xl xl:px-0">
              <div className="grid grid-cols-8 gap-6 md:gap-0">
                <div className="col-span-8 md:col-span-2 lg:col-span-3">
                  <div className="flex h-full items-center justify-between gap-6 border-b border-white py-6 dark:border-gray-800 md:flex-col md:items-start md:justify-between md:space-y-6 md:border-none md:py-0">
                    <div className="flex gap-6">
                      <a
                        target="blank"
                        aria-label="twitter"
                        href="https://twitter.com/UseBackupDiary"
                        className="hover:text-blue-600 dark:hover:text-blue-500"
                      >
                        <span className="sr-only">Twitter</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          fill="currentColor"
                          className="bi bi-twitter"
                          viewBox="0 0 16 16"
                        >
                          <path d="M5.026 15c6.038 0 9.341-5.003 9.341-9.334 0-.14 0-.282-.006-.422A6.685 6.685 0 0 0 16 3.542a6.658 6.658 0 0 1-1.889.518 3.301 3.301 0 0 0 1.447-1.817 6.533 6.533 0 0 1-2.087.793A3.286 3.286 0 0 0 7.875 6.03a9.325 9.325 0 0 1-6.767-3.429 3.289 3.289 0 0 0 1.018 4.382A3.323 3.323 0 0 1 .64 6.575v.045a3.288 3.288 0 0 0 2.632 3.218 3.203 3.203 0 0 1-.865.115 3.23 3.23 0 0 1-.614-.057 3.283 3.283 0 0 0 3.067 2.277A6.588 6.588 0 0 1 .78 13.58a6.32 6.32 0 0 1-.78-.045A9.344 9.344 0 0 0 5.026 15z" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="col-span-8 md:col-span-6 lg:col-span-5">
                  <div className="flex justify-between text-sm">
                    <div className="space-y-4">
                      <div className="space-x-4">
                        <Link href={'/terms'}>Terms of Service</Link>
                        <Link href={'/privacy'}>Privacy Policy</Link>
                      </div>
                      <span>
                        All rights reserved - &copy; Backup Diary 2026
                      </span>
                    </div>
                    <button
                      onClick={changeTheme}
                      aria-label="switch theme"
                      className="group relative h-9 w-9 rounded-full before:absolute before:inset-0 before:rounded-full before:border before:border-gray-200 before:bg-gray-50 before:bg-gradient-to-b before:transition-transform before:duration-300 hover:before:scale-105 active:duration-75 active:before:scale-95 dark:before:border-gray-700 dark:before:bg-gray-800 lg:flex"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="transistion relative m-auto hidden h-5 w-5 fill-gray-500 duration-300 group-hover:rotate-180 group-hover:fill-yellow-400 dark:block dark:fill-gray-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="transistion relative m-auto h-5 w-5 fill-gray-500 duration-300 group-hover:-rotate-90 group-hover:fill-blue-900 dark:hidden"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
      <ChatWidget />
    </>
  )
}
